/**
 * MultiShotCamera.js
 * Встроенная камера (getUserMedia) для серийной съёмки чеков.
 *
 * Поток: открывается live-видео внутри PWA → юзер делает несколько кадров подряд
 * (камера не закрывается) → «Готово (N)» отдаёт все File[] наверх скопом.
 *
 * Откат на нативную камеру: этот компонент вообще не открывается — см.
 * isMultiShotSupported() в ExpensesSection.js. При любой ошибке getUserMedia
 * вызывается onUnsupported() → вызывающий код открывает нативный <input capture>.
 *
 * Props:
 *   maxShots      — сколько кадров можно снять в этой сессии (остаток лимита)
 *   onDone(items)  — массив { file: File(image/jpeg), comment: string }, пользователь подтвердил
 *   onClose()     — закрыл без подтверждения (кадры отбрасываются)
 *   onUnsupported(reason) — камеру не удалось запустить → фолбэк на нативный input
 *   context       — строка для аналитики ('fab' | 'inline' | ...)
 *   allowComment  — показывать кнопку «Комментарий» (по умолчанию выкл; для чеков не нужно)
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { track } from '../analytics';

const COMMENT_MAX = 100;

export default function MultiShotCamera({ maxShots = 4, onDone, onClose, onUnsupported, context = 'unknown', allowComment = false }) {
  const { t } = useTranslation();
  const rootRef   = useRef(null); // контейнер портала — на нём ловим pinch
  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const trackRef  = useRef(null); // video track — для applyConstraints (зум)
  const shotsRef  = useRef([]); // держим File[] в ref, чтобы cleanup на unmount видел актуальное
  const closedRef = useRef(false);
  // Зум: троттлинг applyConstraints + состояние pinch-жеста
  const zoomRef        = useRef(1);
  const zoomBusyRef    = useRef(false);
  const zoomPendingRef = useRef(null);
  const pinchRef       = useRef(null); // { startDist, startZoom } во время жеста

  const [shots,  setShots]  = useState([]); // [{ url, file, comment }]
  const [ready,  setReady]  = useState(false);
  const [busy,   setBusy]   = useState(false); // блок затвора во время toBlob
  const [commentIdx, setCommentIdx] = useState(null); // индекс кадра в режиме комментария; null = панель закрыта
  const [zoomCaps, setZoomCaps] = useState(null); // { min, max, step } если железо поддерживает; иначе null
  const [zoom, setZoom] = useState(1);

  // ── Запуск потока ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width:  { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(tr => tr.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        // Оптический/цифровой зум — только если трек отдаёт capabilities.zoom
        // (Android-Chrome часто умеет; iOS Safari обычно нет → кнопки не покажем).
        const vTrack = stream.getVideoTracks()[0] || null;
        trackRef.current = vTrack;
        try {
          const caps = vTrack && typeof vTrack.getCapabilities === 'function' ? vTrack.getCapabilities() : null;
          if (caps && caps.zoom && typeof caps.zoom.max === 'number' && caps.zoom.max > (caps.zoom.min || 1)) {
            const settings = typeof vTrack.getSettings === 'function' ? vTrack.getSettings() : {};
            const z0 = settings.zoom || caps.zoom.min || 1;
            setZoomCaps({ min: caps.zoom.min || 1, max: caps.zoom.max, step: caps.zoom.step || 0.1 });
            setZoom(z0);
            zoomRef.current = z0;
          }
        } catch { /* capabilities недоступны — просто без зума */ }
        setReady(true);
        track('receipt_camera_opened', { context, max_shots: maxShots });
      } catch (e) {
        // Нет доступа / не поддерживается / Android-приколы → фолбэк
        track('receipt_camera_unsupported', { reason: e?.name || 'error', context });
        onUnsupported && onUnsupported(e?.name || 'error');
      }
    })();

    return () => {
      cancelled = true;
      stopStream();
      // подчищаем objectURL'ы
      shotsRef.current.forEach(s => { try { URL.revokeObjectURL(s.url); } catch {} });
    };
    // eslint-disable-next-line
  }, []);

  const stopStream = useCallback(() => {
    const s = streamRef.current;
    if (s) { s.getTracks().forEach(tr => tr.stop()); streamRef.current = null; }
    trackRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  // ── Зум ──────────────────────────────────────────────────────────────────
  const clampZoom = useCallback((v) => {
    if (!zoomCaps) return 1;
    return Math.min(zoomCaps.max, Math.max(zoomCaps.min, Math.round(v * 10) / 10));
  }, [zoomCaps]);

  // Низкоуровневое применение зума с троттлингом: applyConstraints асинхронный,
  // pinch шлёт значения пачками — держим максимум один запрос в полёте, остальное
  // схлопываем в «последнее желаемое». Без PostHog (событие шлём отдельно).
  const commitZoom = useCallback((v) => {
    const trk = trackRef.current;
    if (!trk || !zoomCaps) return;
    const clamped = clampZoom(v);
    if (zoomBusyRef.current) { zoomPendingRef.current = clamped; return; }
    zoomBusyRef.current = true;
    trk.applyConstraints({ advanced: [{ zoom: clamped }] })
      .then(() => { zoomRef.current = clamped; setZoom(clamped); })
      .catch(() => { /* устройство отклонило значение — игнор */ })
      .finally(() => {
        zoomBusyRef.current = false;
        if (zoomPendingRef.current != null) {
          const p = zoomPendingRef.current; zoomPendingRef.current = null; commitZoom(p);
        }
      });
  }, [zoomCaps, clampZoom]);

  // Кнопки +/- : применяем и сразу шлём событие
  const applyZoom = useCallback((next) => {
    if (!zoomCaps) return;
    commitZoom(next);
    track('camera_zoom_changed', { zoom: clampZoom(next), source: 'button', context });
  }, [zoomCaps, commitZoom, clampZoom, context]);

  // Pinch: запираем масштабирование страницы внутри камеры и (если железо
  // умеет зум) разводим/сводим пальцы → applyConstraints. Нативные listener'ы
  // с passive:false — иначе preventDefault не остановит зум страницы в Safari.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const dist = (touches) => {
      const a = touches[0], b = touches[1];
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    };
    const onStart = (e) => {
      if (e.touches.length === 2 && zoomCaps) {
        pinchRef.current = { startDist: dist(e.touches) || 1, startZoom: zoomRef.current };
      }
    };
    const onMove = (e) => {
      if (e.touches.length >= 2) {
        e.preventDefault(); // глушим зум страницы (кнопка фото и пр. больше не уезжают)
        const p = pinchRef.current;
        if (zoomCaps && p) commitZoom(p.startZoom * (dist(e.touches) / p.startDist));
      }
    };
    const onEnd = (e) => {
      if (pinchRef.current && e.touches.length < 2) {
        pinchRef.current = null;
        track('camera_zoom_changed', { zoom: zoomRef.current, source: 'pinch', context });
      }
    };
    // iOS Safari дополнительно стреляет gesture*-событиями — глушим зум страницы.
    const onGesture = (e) => { e.preventDefault(); };
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    el.addEventListener('gesturestart', onGesture, { passive: false });
    el.addEventListener('gesturechange', onGesture, { passive: false });
    el.addEventListener('gestureend', onGesture, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('gesturestart', onGesture);
      el.removeEventListener('gesturechange', onGesture);
      el.removeEventListener('gestureend', onGesture);
    };
  }, [zoomCaps, commitZoom, context]);

  // ── Затвор ─────────────────────────────────────────────────────────────
  const takeShot = useCallback(() => {
    const video = videoRef.current;
    if (!video || !ready || busy) return;
    if (shotsRef.current.length >= maxShots) return;

    setBusy(true);
    const w = video.videoWidth, h = video.videoHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) { setBusy(false); return; }
      const file = new File([blob], `receipt_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const url  = URL.createObjectURL(blob);
      const next = [...shotsRef.current, { url, file, comment: '' }];
      shotsRef.current = next;
      setShots(next);
      setBusy(false);
      track('receipt_camera_shot', { index: next.length });
    }, 'image/jpeg', 0.95);
  }, [ready, busy, maxShots]);

  const removeShot = useCallback((idx) => {
    const target = shotsRef.current[idx];
    if (target) { try { URL.revokeObjectURL(target.url); } catch {} }
    const next = shotsRef.current.filter((_, i) => i !== idx);
    shotsRef.current = next;
    setShots(next);
    setCommentIdx(null); // индексы сдвинулись — закрываем панель комментария
  }, []);

  // ── Комментарий к кадру ──────────────────────────────────────────────────
  const setCommentFor = useCallback((idx, text) => {
    const clipped = (text || '').slice(0, COMMENT_MAX);
    const next = shotsRef.current.map((s, i) => i === idx ? { ...s, comment: clipped } : s);
    shotsRef.current = next;
    setShots(next);
  }, []);

  // Открыть панель комментария: по умолчанию к последнему кадру, либо к указанному
  const openComment = useCallback((idx) => {
    const target = typeof idx === 'number' ? idx : (shotsRef.current.length - 1);
    if (target < 0) return;
    setCommentIdx(target);
    track('camera_comment_opened', { index: target + 1, context });
  }, [context]);

  const closeComment = useCallback(() => {
    setCommentIdx((cur) => {
      if (cur != null) {
        const c = shotsRef.current[cur]?.comment || '';
        track('camera_comment_saved', { index: cur + 1, has_text: c.length > 0, length: c.length, context });
      }
      return null;
    });
  }, [context]);

  // ── Подтверждение / отмена ───────────────────────────────────────────────
  const handleDone = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    stopStream();
    const items = shotsRef.current.map(s => ({ file: s.file, comment: s.comment || '' }));
    track('receipt_camera_confirmed', {
      count: items.length,
      with_comment: items.filter(i => i.comment).length,
      context,
    });
    // objectURL'ы отдаём дальше как File — превью пересоздаст вызывающий код,
    // наши url'ы чистит cleanup в useEffect
    onDone && onDone(items);
  }, [onDone, stopStream, context]);

  const handleClose = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    stopStream();
    track('receipt_camera_cancelled', { count: shotsRef.current.length, context });
    onClose && onClose();
  }, [onClose, stopStream, context]);

  // Escape — закрыть панель комментария, иначе закрыть камеру
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (commentIdx != null) closeComment();
      else handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose, closeComment, commentIdx]);

  const limitReached = shots.length >= maxShots;
  const remaining = Math.max(0, maxShots - shots.length);
  // Шаг зума: ~6 нажатий на весь диапазон, но не мельче, чем шаг устройства
  const zoomStep = zoomCaps ? Math.max(zoomCaps.step, Math.round(((zoomCaps.max - zoomCaps.min) / 6) * 10) / 10) : 0;
  const zoomBtn = {
    width: 40, height: 40, borderRadius: '50%', border: 'none',
    background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: 22, fontWeight: 600,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
  };

  return createPortal(
    <div ref={rootRef} style={{
      position: 'fixed', inset: 0, zIndex: 4000,
      background: '#000', display: 'flex', flexDirection: 'column',
      touchAction: 'none', // запрет нативного pan/zoom внутри камеры
    }}>
      {/* Верхняя панель */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'max(12px, env(safe-area-inset-top, 12px)) 14px 12px',
        background: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0))',
      }}>
        <button onClick={handleClose} aria-label={t('common.cancel')} style={{
          width: 38, height: 38, borderRadius: '50%', border: 'none',
          background: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: 20,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>×</button>
        <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>
          {t('expenses.camera.shotsLeft', { n: remaining })}
        </span>
        <div style={{ width: 38 }} />
      </div>

      {/* Видеопоток */}
      <video
        ref={videoRef}
        playsInline
        muted
        style={{ flex: 1, width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
      />

      {!ready && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 14,
        }}>
          {t('expenses.camera.starting')}
        </div>
      )}

      {/* Зум — только если железо отдаёт capabilities.zoom */}
      {ready && zoomCaps && (
        <div style={{
          position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        }}>
          <button onClick={() => applyZoom(zoom + zoomStep)} disabled={zoom >= zoomCaps.max}
            aria-label={t('expenses.camera.zoomIn')} style={{ ...zoomBtn, opacity: zoom >= zoomCaps.max ? 0.4 : 1 }}>+</button>
          <span style={{
            color: '#fff', fontSize: 12, fontWeight: 700,
            background: 'rgba(0,0,0,0.45)', borderRadius: 12, padding: '3px 8px', minWidth: 38, textAlign: 'center',
          }}>{zoom.toFixed(1)}×</span>
          <button onClick={() => applyZoom(zoom - zoomStep)} disabled={zoom <= zoomCaps.min}
            aria-label={t('expenses.camera.zoomOut')} style={{ ...zoomBtn, opacity: zoom <= zoomCaps.min ? 0.4 : 1 }}>−</button>
        </div>
      )}

      {/* Нижняя панель: лента превью + затвор + Готово */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
        padding: '14px 14px max(16px, env(safe-area-inset-bottom, 16px))',
        background: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.55))',
      }}>
        {/* Лента отснятого */}
        {shots.length > 0 && (
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 14, paddingBottom: 2, touchAction: 'pan-x' }}>
            {shots.map((s, i) => (
              <div key={s.url} style={{ position: 'relative', flexShrink: 0 }}>
                <img
                  src={s.url}
                  alt=""
                  onClick={allowComment ? () => openComment(i) : undefined}
                  style={{
                    width: 54, height: 54, objectFit: 'cover', borderRadius: 8,
                    border: '2px solid #fff', cursor: allowComment ? 'pointer' : 'default',
                  }}
                />
                {allowComment && s.comment && (
                  <span aria-hidden="true" style={{
                    position: 'absolute', bottom: -4, left: -4,
                    width: 18, height: 18, borderRadius: '50%',
                    background: 'var(--accent, #6366f1)', color: '#fff', fontSize: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                    border: '1.5px solid #fff',
                  }}>💬</span>
                )}
                <button onClick={() => removeShot(i)} aria-label={t('photos.delete')} style={{
                  position: 'absolute', top: -6, right: -6,
                  width: 20, height: 20, borderRadius: '50%', border: 'none',
                  background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: 12,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                }}>×</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Комментарий (опц.) — к последнему кадру; либо спейсер для симметрии */}
          {allowComment ? (
            <button
              onClick={() => openComment()}
              disabled={shots.length === 0}
              aria-label={t('expenses.camera.addComment')}
              style={{
                width: 88, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                background: 'none', border: 'none',
                color: shots.length === 0 ? 'rgba(255,255,255,0.35)' : '#fff',
                cursor: shots.length === 0 ? 'default' : 'pointer',
              }}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
              </svg>
              <span style={{ fontSize: 11, fontWeight: 600 }}>{t('expenses.camera.addComment')}</span>
            </button>
          ) : (
            <div style={{ width: 88 }} />
          )}

          {/* Затвор */}
          <button
            onClick={takeShot}
            disabled={!ready || busy || limitReached}
            aria-label={t('photos.camera')}
            style={{
              width: 70, height: 70, borderRadius: '50%',
              border: '4px solid rgba(255,255,255,0.85)',
              background: limitReached ? 'rgba(255,255,255,0.3)' : '#fff',
              cursor: (!ready || limitReached) ? 'default' : 'pointer',
              boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
              transition: 'transform 0.08s', transform: busy ? 'scale(0.92)' : 'scale(1)',
            }}
          />

          {/* Готово */}
          <button
            onClick={handleDone}
            disabled={shots.length === 0}
            style={{
              width: 88, padding: '10px 0', borderRadius: 22, border: 'none',
              background: shots.length === 0 ? 'rgba(255,255,255,0.25)' : 'var(--accent, #6366f1)',
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: shots.length === 0 ? 'default' : 'pointer',
            }}
          >
            {t('expenses.camera.done', { n: shots.length })}
          </button>
        </div>
      </div>

      {/* Панель комментария — вверху, чтобы клавиатура (с микрофоном) её не перекрывала */}
      {commentIdx != null && shots[commentIdx] && (
        <div
          onClick={closeComment}
          style={{
            position: 'absolute', inset: 0, zIndex: 3,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: 'max(56px, calc(env(safe-area-inset-top, 12px) + 48px)) 14px 0',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 480, background: '#fff', borderRadius: 14,
              padding: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={shots[commentIdx].url} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>
                {t('expenses.camera.commentFor', { n: commentIdx + 1 })}
              </span>
            </div>
            <textarea
              autoFocus
              rows={2}
              value={shots[commentIdx].comment || ''}
              onChange={e => setCommentFor(commentIdx, e.target.value)}
              maxLength={COMMENT_MAX}
              placeholder={t('expenses.camera.commentPlaceholder')}
              style={{
                width: '100%', boxSizing: 'border-box', resize: 'none',
                border: '1.5px solid var(--border2, #e0e0e0)', borderRadius: 8,
                padding: '10px 12px', fontSize: 16, fontFamily: 'inherit', color: '#111', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, color: '#999' }}>
                {(shots[commentIdx].comment || '').length} {t('photos.of')} {COMMENT_MAX}
              </span>
              <button
                onClick={closeComment}
                style={{
                  padding: '8px 18px', borderRadius: 18, border: 'none',
                  background: 'var(--accent, #6366f1)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                }}
              >
                {t('common.done')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
